<template>
  <figure class="interactive__figure" :class="{ 'interactive__figure--magnification': magnification }" :style="interactiveFigureStyle">
    <svg
      ref="svgEl"
      class="interactive__svg"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      :viewBox="imageNaturalWidth && imageNaturalHeight ? `0 0 ${imageNaturalWidth} ${imageNaturalHeight}` : undefined"
      :aria-label="`alt-${idPrefix}`"
      :aria-describedby="`figcaption-${idPrefix}`">

      <image
        :width="imageNaturalWidth || '100%'"
        :height="imageNaturalHeight || '100%'"
        :alt="altText"
        :xlink:href="currentMediaAssetData ? currentMediaAssetData.url : ''"
        @load="onSvgImageLoad" />

      <vehicle-selector-function-info-interactive-map-area
        v-for="item in orderedAreas"
        :key="item.originalIndex"
        :item="item"
        :circle-radius="circleRadius"
        :image-natural-width="imageNaturalWidth"
        :image-natural-height="imageNaturalHeight"
        :hovered-shape-index="hoveredShapeIndex"
        @area-mouseenter="handleMouseOver"
        @area-mousemove="handleMouseMove"
        @area-mouseleave="handleMouseLeaveSpot"
        @area-click="handleAreaClick" />

    </svg>
    <div v-if="isLoading" class="interactive__loading"></div>
    <oie-base-action
      v-if="mediaAssetsUrl.length > 1 && selectedImageIndex > 0"
      :aria-label="tStatic('previous')"
      theme="outlined-black"
      round
      class="vehicle-selector-function-info__nav-button vehicle-selector-function-info__nav-button--prev"
      @click.stop="selectPrevImage">
      <oie-base-icon icon="chevron-left"></oie-base-icon>
    </oie-base-action>
    <oie-base-action
      v-if="mediaAssetsUrl.length > 1 && selectedImageIndex < mediaAssetsUrl.length - 1"
      :aria-label="tStatic('next')"
      theme="outlined-black"
      round
      class="vehicle-selector-function-info__nav-button vehicle-selector-function-info__nav-button--next"
      @click.stop="selectNextImage">
      <oie-base-icon icon="chevron-right"></oie-base-icon>
    </oie-base-action>
    <button
      v-if="!magnification"
      class="vehicle-selector-function-info__zoom-icon"
      aria-label="Zoom image"
      @click="openInspector">
      <oie-use-svg svg="zoom"></oie-use-svg>
    </button>

    <vehicle-selector-function-info-interactive-map-dialog
      v-if="selectedShape"
      ref="dialogComponent"
      :selected-shape="selectedShape"
      :products="products"
      :id-prefix="idPrefix"
      :position="dialogPosition"
      :opens-to-bottom="dialogOpensToBottom"
      @pointer-enter="pointerInDialog = true"
      @pointer-leave="handleMouseLeaveDialog">
    </vehicle-selector-function-info-interactive-map-dialog>
  </figure>
</template>

<script>
import { defineComponent, computed, ref, watch, onMounted, onUnmounted, nextTick } from '@vue/composition-api'
import VehicleSelectorFunctionInfoInteractiveMapDialog from './vehicle-selector-function-info-interactive-map-dialog.vue'
import VehicleSelectorFunctionInfoInteractiveMapArea from './vehicle-selector-function-info-interactive-map-area.vue'

export default defineComponent({
  components: { VehicleSelectorFunctionInfoInteractiveMapDialog, VehicleSelectorFunctionInfoInteractiveMapArea },
  props: {
    mediaAssetsData: {
      type: Array,
      required: true
    },
    mediaAssetsUrl: {
      type: Array,
      required: true
    },
    selectedImageIndex: {
      type: Number,
      default: 0
    },
    altText: {
      type: String,
      default: ''
    },
    idPrefix: {
      type: String,
      required: true
    },
    products: {
      type: Array,
      default: () => []
    },
    magnification: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const dialogComponent = ref(null)
    const svgEl = ref(null)
    const svgRenderedWidth = ref(0)
    const hotspot = ref()
    const dialogCloser = ref()
    const selectedShape = ref()
    const dialogPosition = ref({ top: '0px', left: '0px' })
    const dialogOpensToBottom = ref(false)
    const pointerInDialog = ref(false)
    const hoveredShapeIndex = ref(null)
    let cooldownActive = false
    let cooldownTimer = null
    let pendingHover = null

    const imageNaturalWidth = ref(0)
    const imageNaturalHeight = ref(0)
    const isLoading = ref(true)

    const loadSvgImageDimensions = (url) => {
      if (!url) return
      isLoading.value = true
      const img = new Image()
      img.onload = () => {
        imageNaturalWidth.value = img.naturalWidth
        imageNaturalHeight.value = img.naturalHeight
        isLoading.value = false
      }
      img.src = url
    }

    const getSvgImageUrl = () => {
      const currentUrl = props.mediaAssetsUrl && props.mediaAssetsUrl[props.selectedImageIndex]
      if (!currentUrl || !props.mediaAssetsData) return null
      const match = props.mediaAssetsData.find(d => d.url && d.url.includes(currentUrl))
      return match ? match.url : null
    }

    let resizeObserver = null

    const onScroll = () => {
      if (selectedShape.value && hotspot.value) {
        positionDialogToTarget()
      }
    }

    onMounted(() => {
      const url = getSvgImageUrl()
      if (url) loadSvgImageDimensions(url)
      if (svgEl.value) {
        resizeObserver = new ResizeObserver(entries => {
          svgRenderedWidth.value = entries[0].contentRect.width
        })
        resizeObserver.observe(svgEl.value)
      }
      window.addEventListener('scroll', onScroll, { passive: true })
    })

    onUnmounted(() => {
      if (resizeObserver) resizeObserver.disconnect()
      window.removeEventListener('scroll', onScroll)
      if (cooldownTimer) clearTimeout(cooldownTimer)
    })

    watch(() => {
      const currentUrl = props.mediaAssetsUrl && props.mediaAssetsUrl[props.selectedImageIndex]
      if (!currentUrl || !props.mediaAssetsData) return null
      const match = props.mediaAssetsData.find(d => d.url && d.url.includes(currentUrl))
      return match ? match.url : null
    }, (newUrl) => {
      if (newUrl) loadSvgImageDimensions(newUrl)
    })

    const onSvgImageLoad = () => {
      if (!imageNaturalWidth.value || !imageNaturalHeight.value) {
        const url = getSvgImageUrl()
        if (url) loadSvgImageDimensions(url)
      }
    }

    const circleRadius = computed(() => {
      if (!imageNaturalWidth.value || !svgRenderedWidth.value) return 20
      return Math.round(20 * imageNaturalWidth.value / svgRenderedWidth.value)
    })

    const currentMediaAssetData = computed(() => {
      const currentUrl = props.mediaAssetsUrl && props.mediaAssetsUrl[props.selectedImageIndex]
      if (!currentUrl || !props.mediaAssetsData) return null
      return props.mediaAssetsData.find(d => d.url && d.url.includes(currentUrl)) || null
    })

    const orderedAreas = computed(() => {
      const data = currentMediaAssetData.value
      if (!data || !data.merchendiseImageMap) return []
      const areas = data.merchendiseImageMap.areas
      if (!areas) return []
      const indexed = areas.map((shape, originalIndex) => ({ shape, originalIndex }))

      if (hoveredShapeIndex.value === null) return indexed
      return [
        ...indexed.filter(({ originalIndex }) => originalIndex !== hoveredShapeIndex.value),
        indexed[hoveredShapeIndex.value]
      ]
    })

    const interactiveFigureStyle = computed(() => {
      if (!isLoading.value && imageNaturalWidth.value && imageNaturalHeight.value) {
        return { aspectRatio: `${imageNaturalWidth.value} / ${imageNaturalHeight.value}` }
      }
      return { aspectRatio: '1 / 1' }
    })

    const openDialog = async (event, shape) => {
      if (!event.target) return
      hotspot.value = event.target
      selectedShape.value = shape
      await nextTick()
      positionDialogToTarget()
      await nextTick()
      dialogComponent.value && dialogComponent.value.show()
    }

    const positionDialogToTarget = () => {
      const positioningOffset = 3

      const dialogWidth = dialogComponent.value ? dialogComponent.value.$el.offsetWidth : 0
      const dialogHeight = dialogComponent.value ? dialogComponent.value.$el.offsetHeight : 0
      const viewportWidth = window.innerWidth

      const rect = hotspot.value.getBoundingClientRect()
      const figureRect = svgEl.value ? svgEl.value.getBoundingClientRect() : rect

      const hotspotCenterY = rect.top + rect.height / 2
      const figureCenterY = figureRect.top + figureRect.height / 2
      const opensBelow = hotspotCenterY < figureCenterY

      dialogOpensToBottom.value = opensBelow
      dialogPosition.value.top = opensBelow
        ? `${rect.bottom + positioningOffset}px`
        : `${rect.top - dialogHeight - positioningOffset}px`

      const dialogOverflowsToRight = rect.right + dialogWidth > viewportWidth
      dialogPosition.value.left = dialogOverflowsToRight
        ? `${rect.left - dialogWidth - positioningOffset}px`
        : `${rect.right + positioningOffset}px`
    }

    const handleMouseOver = (event, shape, index) => {
      if (cooldownActive) {
        pendingHover = { target: event.target, shape, index }
        return
      }
      if (dialogCloser.value) clearTimeout(dialogCloser.value)
      hoveredShapeIndex.value = index
      openDialog(event, shape)
    }

    const handleMouseMove = (event, shape, index) => {
      if (cooldownActive) {
        pendingHover = { target: event.target, shape, index }
        return
      }
      if (selectedShape.value === shape) return
      if (dialogCloser.value) clearTimeout(dialogCloser.value)
      hoveredShapeIndex.value = index
      openDialog(event, shape)
    }

    const handleMouseLeaveSpot = () => {
      pendingHover = null
      hoveredShapeIndex.value = null
      automaticCloseDialog()
      if (!cooldownActive) {
        cooldownActive = true
        cooldownTimer = setTimeout(() => {
          cooldownActive = false
          cooldownTimer = null
          if (pendingHover) {
            const { target, shape, index } = pendingHover
            pendingHover = null
            if (dialogCloser.value) clearTimeout(dialogCloser.value)
            hoveredShapeIndex.value = index
            openDialog({ target }, shape)
          }
        }, 150)
      }
    }

    const automaticCloseDialog = () => {
      if (dialogCloser.value) clearTimeout(dialogCloser.value)
      dialogCloser.value = setTimeout(() => {
        if (pointerInDialog.value) return
        closeDialog()
      }, 300)
    }

    const closeDialog = () => {
      dialogComponent.value && dialogComponent.value.close()
      selectedShape.value = null
    }

    const handleMouseLeaveDialog = () => {
      pointerInDialog.value = false
      automaticCloseDialog()
    }

    const gotoUrl = (url) => {
      if (!url) return
      window.location.href = url
    }

    const handleAreaClick = (shape) => {
      const url = (props.products.find(p => p.id === shape.code) || {}).url
      const categoryUrl = shape.categoryUrl
      if (shape.categoryUrl) {
        gotoUrl(categoryUrl)
      } else if (url) {
        gotoUrl(url)
      }
    }

    const selectPrevImage = () => emit('select-image', props.selectedImageIndex - 1)
    const selectNextImage = () => emit('select-image', props.selectedImageIndex + 1)
    const openInspector = () => emit('open-inspector')

    return {
      dialogComponent,
      svgEl,
      selectedShape,
      dialogPosition,
      dialogOpensToBottom,
      pointerInDialog,
      hoveredShapeIndex,
      imageNaturalWidth,
      imageNaturalHeight,
      isLoading,
      onSvgImageLoad,
      orderedAreas,
      interactiveFigureStyle,
      circleRadius,
      currentMediaAssetData,
      handleMouseOver,
      handleMouseMove,
      handleMouseLeaveSpot,
      handleMouseLeaveDialog,
      handleAreaClick,
      gotoUrl,
      selectPrevImage,
      selectNextImage,
      openInspector
    }
  }
})
</script>

<style lang="scss">
.interactive__figure {
  position: relative;
  width: 100%;
  flex: 1;
  min-width: 0;
  vertical-align: middle;
  margin: 0;
  overflow: hidden;
  border: 1px solid color(border-blue-3);
  border-radius: 4px;
  &:hover {
    border-color: color(border-blue-1);
  }

  &:hover .vehicle-selector-function-info__zoom-icon,
  &:hover .vehicle-selector-function-info__nav-button {
    opacity: 1;
  }

  .vehicle-selector-function-info__zoom-icon {
    pointer-events: auto;
  }
}

.vehicle-selector-function-info__zoom-icon:focus {
  opacity: 1;
  outline: 1px solid #222222;
  outline-offset: 2px;
}

.interactive__figure--magnification {
  border: none;
  &:hover {
    border: none;
  }
}

.modal-image-inspector__image-container:has(.interactive__figure--magnification) {
  .modal-image-inspector__button {
    z-index: 1;
  }
}

.interactive__loading {
  position: absolute;
  inset: 0;
  background-color: white;
  z-index: 1;
}

.interactive__svg {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
